import {
  FieldAddonDropdown,
  InputText,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { unwrapResult } from '@reduxjs/toolkit';
import { boundMethod } from 'autobind-decorator';
import { SearchUser } from 'lib-search-types';
import { accessControl } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import { Country } from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import filter from 'lodash/filter';
import find from 'lodash/find';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  containsNumber,
  getPhoneNumberDetails,
  isNumberValid,
  numberFormatterE164,
} from 'app/utils/telephonyHelpers';
import { Autocomplete } from 'components/shared/util/autocomplete';
import { phoneCallDisabledTooltipMessages } from 'components/telephony/utils/phoneCallAvailability';
import { AddUserPayload } from 'components/usersModal';

import { SearchUserInjectedProps } from '../../containers/searchUser';
import { CountryCodeDropdownItem } from '../CountryCodeDropdownItem';
import { CountryCodeSelected } from '../CountryCodeSelected';
import ListUsers from '../ListUsers';

import styles from './searchUser.module.scss';
import '../../styles/search-users-to-call.less';
import { spreadPhoneUsers } from './utils/spreadPhoneUsers';
import type { PhoneUserDuplicate } from './utils/spreadPhoneUsers';

interface TelephonySearchUserOwnProps {
  fromNumberFormat?: string | null;
  canMakeOutBoundCall?: boolean;
  isOwnNumbersViewOpen?: boolean;
  isResolvingDefaultNumber?: boolean;
  // Reason text from `getPhoneCallAvailability`. When the gate is closed,
  // we surface this so the dialer's tooltip matches the contact card / user
  // info tooltips ("Enable the microphone…", "End your current call…", etc.)
  // instead of the historic generic "Can't make outbound call".
  phoneCallDisabledTooltipText?: string;
  toggleOwnNumbersView: (isOpen: boolean) => void;
}

interface CountryDropdownItem extends Omit<Country, 'iso' | 'name'> {
  id: string;
  name: React.ReactChild;
}

type PropsT = TelephonySearchUserOwnProps & SearchUserInjectedProps;

// The stored search value has the active country code prepended (see
// setSearchValue). For display we strip that prefix back off. An empty prefix
// means the user cleared the country code (backspace), in which case we must
// return the full value unchanged — splitting on an empty string would yield
// one character per element and collapse the input to the last character.
export function stripCountryCodePrefix(
  searchValue: string,
  countryCodeSelected: string,
): string {
  if (!countryCodeSelected) return searchValue;
  if (!searchValue.startsWith(countryCodeSelected)) return searchValue;
  return searchValue.slice(countryCodeSelected.length);
}

type StateT = {
  highlightedIndex: number | null;
  phoneUsers: Array<PhoneUserDuplicate>;
  countryISOSelected: string;
  countryCodeSelected: string;
  countryDropdownOpen: boolean;
};

export class TelephonySearchUser extends React.Component<PropsT, StateT> {
  autocomplete: Autocomplete;
  input: React.RefObject<HTMLInputElement>;

  constructor(props: PropsT) {
    super(props);
    this.input = React.createRef();
    this.state = {
      highlightedIndex: null,
      phoneUsers: [],
      countryISOSelected: '',
      countryCodeSelected: '',
      countryDropdownOpen: false,
    };

    this.autocomplete = new Autocomplete(this._requestGetUsers);
  }

  componentDidMount() {
    this.updateUserCountryCodeDetails();
  }

  UNSAFE_componentWillReceiveProps(nextProps: PropsT) {
    // If `users` has changed we want to reset `highlightedIndex`
    // since it probably no longer refers to a relevant item.
    //
    // The entries in `users` may have been changed even though the
    // object reference remains the same, double check by seeing
    // if `highlightedIndex` points to an existing item.

    let phoneUsers: Array<SearchUser> = [];
    let nextPhoneUsers: Array<SearchUser> = [];
    if (nextProps.searchValue.length > 0) {
      if (nextProps.searchValue !== this.props.searchValue) {
        this.autocomplete.search(nextProps.searchValue);
      }

      if (this.props.users.length > 0) {
        phoneUsers = this.props.users.filter(
          (user: SearchUser) =>
            typeof user.phone_number === 'string' && user.phone_number !== '',
        );
      }

      if (nextProps.users.length > 0) {
        nextPhoneUsers = nextProps.users.filter(
          (user: SearchUser) =>
            typeof user.phone_number === 'string' && user.phone_number !== '',
        );
      }

      if (
        phoneUsers?.length !== nextPhoneUsers?.length ||
        (this.state.highlightedIndex &&
          this.state.highlightedIndex >= nextPhoneUsers.length)
      ) {
        this.setState({
          highlightedIndex: null,
          phoneUsers: spreadPhoneUsers(nextPhoneUsers),
        });
      } else {
        this.setState({
          phoneUsers: spreadPhoneUsers(nextPhoneUsers),
        });
      }
    } else {
      this.setState({
        phoneUsers: spreadPhoneUsers(phoneUsers),
      });
    }
  }

  componentDidUpdate(prevProps: PropsT) {
    if (!this.state.countryDropdownOpen && !this.props.isOwnNumbersViewOpen) {
      this.focusInput();
    }

    const didCloseOwnNumbersView =
      prevProps.isOwnNumbersViewOpen === true &&
      this.props.isOwnNumbersViewOpen === false;
    const didSelectedFromNumberChange =
      this.props.selectedFromNumber?.e164_format !==
      prevProps.selectedFromNumber?.e164_format;
    const shouldRestoreClearedCountryCode =
      didCloseOwnNumbersView && this.state.countryCodeSelected === '';

    if (didSelectedFromNumberChange || shouldRestoreClearedCountryCode) {
      this.updateUserCountryCodeDetails();
    }
  }

  @boundMethod
  updateUserCountryCodeDetails(): void {
    const selectedFromNumber = this.props.selectedFromNumber
      ?.e164_format as string;
    if (isNumberValid({ phoneNumber: selectedFromNumber })) {
      const phone = getPhoneNumberDetails(selectedFromNumber);
      this.setState({
        countryISOSelected: phone.countryISO,
        countryCodeSelected: phone.countryCode,
      });
    }
  }

  @boundMethod
  focusInput(): void {
    this.input.current?.focus();
  }

  @boundMethod
  setSearchValue(inputValue: string): void {
    this.props.setSearchUserValue(
      `${this.state.countryCodeSelected}${inputValue}`,
    );
  }

  @boundMethod
  handlePhoneNumberInput(inputValue: string): void {
    const { countries } = this.props;
    const inputtedCode = inputValue.substring(1);

    const country = countries.items.find(
      (country) => country.phone === inputtedCode,
    );

    if (country) {
      // US has the same country code as Canada and 23 other countries
      // that are closely connected to the US, we want to show US by
      // default this is the only exception in the logic
      if (country.phone === '1') {
        this.setState({
          countryCodeSelected: '+1',
          countryISOSelected: 'us',
        });
      } else {
        this.setState({
          countryCodeSelected: `+${country.phone}`,
          countryISOSelected: country.iso.toLowerCase(),
        });
      }
    }
    // The user already typed the leading "+code" themselves, so the inputValue
    // is already a full prefixed value. Avoid re-prepending the previous
    // country code (which would race against the setState above and produce a
    // double-prefix like "+44+45...").
    this.props.setSearchUserValue(inputValue);
  }

  @boundMethod
  handleCopyPastePhoneNumber(inputValue: string): void {
    const phone = getPhoneNumberDetails(inputValue);
    this.setState(
      {
        countryCodeSelected: phone.countryCode,
        countryISOSelected: phone.countryISO,
      },
      () => {
        this.props.setSearchUserValue(
          `${this.state.countryCodeSelected}${phone.nationalNumber}`,
        );
      },
    );
  }

  @boundMethod
  onChangeInput(e?: React.ChangeEvent<HTMLInputElement>): void {
    if (!e) return;
    // Prevent multiple spaces
    const inputValue = e.target.value.replace(/\s\s+/g, ' ');

    if (containsNumber(inputValue)) {
      if (
        isNumberValid({ phoneNumber: inputValue }) &&
        inputValue.startsWith('+')
      ) {
        this.handleCopyPastePhoneNumber(inputValue);
      } else if (inputValue.startsWith('+')) {
        this.handlePhoneNumberInput(inputValue);
      } else {
        this.setSearchValue(inputValue);
      }
    } else {
      this.props.setSearchUserValue(inputValue);
    }
  }

  @boundMethod
  _requestGetUsers(searchValue: string): void {
    let searchQuery = searchValue.trim();

    if (searchQuery) {
      const isPhoneLikeSearchQuery =
        containsNumber(searchQuery) && searchQuery.startsWith('+');

      if (isPhoneLikeSearchQuery) {
        // Validate the inputted phone number and format it to E164, to handle cases such as
        // leading zeroes manually typed by the user which should not be included in the search.
        searchQuery = numberFormatterE164({
          phoneNumber: searchQuery,
        });

        const queryWithoutCode = searchQuery.replace(
          this.state.countryCodeSelected,
          '',
        );

        // If the provided query is a phone number, we'll also include the national number without
        // the country code in the search request, which will ensure that we also get results where
        // the user display name contains the phone number.
        if (queryWithoutCode) {
          // In order to match both the display name and the phone number,
          // we separate the queries by space, and the number without code must be first
          searchQuery = `${queryWithoutCode} ${searchQuery}`;
        }
      }

      this.props.getUsersByQuery({ search: searchQuery });
    } else {
      // We reset the phoneUsers result if there is no searchValue.
      this.setState({ phoneUsers: [] });
    }
  }

  @boundMethod
  searchInputKeyDown(e?: React.KeyboardEvent): void {
    if (!e) return;
    //closing Collapsible component
    const phone_number = this.props.searchValue;
    this.props.toggleOwnNumbersView(false);
    if (
      e.which === 8 &&
      this.props.searchValue.trim() === '' &&
      this.state.countryCodeSelected !== ''
    ) {
      // Backspace key pressed.
      this.setState({
        countryCodeSelected: '',
        countryISOSelected: '',
      });

      // Enter key pressed.
    } else if (e.which === 13 && this.props.canMakeOutBoundCall === true) {
      let user;

      // There is nothing selected on highlightedIndex, we try to find the user by the phone_number.
      if (
        this.state.highlightedIndex === null &&
        isNumberValid({ phoneNumber: phone_number }) === true
      ) {
        const phoneNumberE164 = numberFormatterE164({
          phoneNumber: phone_number,
        });
        user = find(this.state.phoneUsers, { phone_number: phoneNumberE164 });
      } else if (
        this.state.highlightedIndex === null &&
        isNumberValid({
          phoneNumber: this.state.countryCodeSelected + phone_number,
        }) === true
      ) {
        const phoneNumberE164 = numberFormatterE164({
          phoneNumber: this.state.countryCodeSelected + phone_number,
        });
        user = find(this.state.phoneUsers, { phone_number: phoneNumberE164 });
      } else if (
        this.state.highlightedIndex === null &&
        isNumberValid({
          phoneNumber: `+${this.state.countryCodeSelected}${phone_number}`,
        }) === true
      ) {
        const phoneNumberE164 = numberFormatterE164({
          phoneNumber: `+${this.state.countryCodeSelected}${phone_number}`,
        });
        user = find(this.state.phoneUsers, { phone_number: phoneNumberE164 });
      } else if (
        this.state.highlightedIndex === null &&
        this.state.phoneUsers.length === 1
      ) {
        user = this.state.phoneUsers[0];
      } else if (this.state.highlightedIndex !== null) {
        user = this.state.phoneUsers[this.state.highlightedIndex];
      }

      if (user !== undefined) {
        // The user already exists try to make the call.
        this.makeCallClickHandler(user);
      } else if (isNumberValid({ phoneNumber: phone_number })) {
        // Try with full phone_number.
        // The user does not exists.
        // We make a new user and call it afterwords.
        user = {
          id: 'newUser',
          avatar_url: null,
          icon: 'phone',
          email: null,
          phone_number: numberFormatterE164({ phoneNumber: phone_number }),
          name: null,
          type: 'contact',
        };
        this.makeCallClickHandler(user);
      } else if (
        isNumberValid({
          phoneNumber: this.state.countryCodeSelected + phone_number,
        })
      ) {
        // Try with full phone_number with a countrycode prefix .
        user = {
          id: 'newUser',
          avatar_url: null,
          icon: 'phone',
          email: null,
          phone_number: numberFormatterE164({
            phoneNumber: this.state.countryCodeSelected + phone_number,
          }),
          name: null,
          type: 'contact',
        };
        this.makeCallClickHandler(user);
      } else if (
        isNumberValid({
          phoneNumber: `+${this.state.countryCodeSelected}${phone_number}`,
        })
      ) {
        // Try with full phone_number with + sign and countrycode as prefix.
        user = {
          id: 'newUser',
          avatar_url: null,
          icon: 'phone',
          email: null,
          phone_number: numberFormatterE164({
            phoneNumber: `+${this.state.countryCodeSelected}${phone_number}`,
          }),
          name: null,
          type: 'contact',
        };
        this.makeCallClickHandler(user);
      } else {
        console.error('Does not have a valid phone number', phone_number);
      }
    } else if (e.which === 40) {
      // Arrow down.
      e.preventDefault();
      try {
        const numberOfOptions = this.state.phoneUsers.length;

        if (!numberOfOptions) {
          return;
        }
        const highlightedIndex = this.state.highlightedIndex;

        const index =
          highlightedIndex === null || highlightedIndex === numberOfOptions - 1
            ? 0
            : highlightedIndex + 1;

        this.setState({ highlightedIndex: index });
      } catch (exception) {}
    } else if (e.which === 38) {
      // Arrow up.
      e.preventDefault();
      try {
        const numberOfOptions = this.state.phoneUsers.length;

        if (!numberOfOptions) {
          return;
        }
        const highlightedIndex = this.state.highlightedIndex;

        const index =
          highlightedIndex === 0 || highlightedIndex === null
            ? numberOfOptions - 1
            : highlightedIndex - 1;

        this.setState({ highlightedIndex: index });
      } catch (exception) {}
    }
  }

  @boundMethod
  makeCallClickHandler(user: SearchUser | null): void {
    if (!user?.phone_number) {
      return;
    }

    if (user.id !== 'newUser' && this.props.selectedFromNumber) {
      this.makeCall({
        userId: user.id,
        toNumber: user.phone_number,
        fromNumber: this.props.selectedFromNumber?.e164_format,
      });
    } else if (this.props.selectedFromNumber) {
      const newEndUser: AddUserPayload = {
        phone_number: user.phone_number.replace(/[\s()-]/g, ''),
        user_type: 'contact',
        email: null,
        additional_emails: [],
        additional_phone_numbers: [],
        name: null,
        given_name: null,
        family_name: null,
        avatar_url: null,
        company_id: null,
      };
      this.props
        .userModalAddUser(newEndUser)
        .then(unwrapResult)
        .then(({ data: newUser }) => {
          this.makeCall({
            userId: newUser.id,
            toNumber: user.phone_number ?? '',
            fromNumber: this.props.selectedFromNumber?.e164_format as string,
          });
        })
        .catch((exception: any) => {
          if (exception && exception.conflicting_id) {
            this.makeCall({
              userId: exception.conflicting_id,
              toNumber: newEndUser.phone_number ?? '',
              fromNumber: this.props.selectedFromNumber?.e164_format as string,
            });
          } else {
            Sentry.captureException(exception, {
              level: 'error',
              extra: {
                note: 'userModalAddUser exception on make call',
              },
            });
          }
        });
    }
  }

  @boundMethod
  makeCall(payload: {
    userId: string;
    toNumber: string;
    fromNumber: string;
  }): void {
    if (this.props.canMakeOutBoundCall !== true) {
      return;
    }

    const { userId, toNumber, fromNumber } = payload;

    if (userId && toNumber && fromNumber) {
      this.props.telephonyMakeCall({
        userId: userId,
        toNumber: toNumber,
        fromNumber: fromNumber,
      });
      this.props.setSearchUserValue('');
      this.props.resetUsers();
    }
  }

  @boundMethod
  onToggle(isOpen: boolean, isFirstOpen: boolean | undefined): void {
    if (isFirstOpen) {
      this.setState({ countryDropdownOpen: true }, () => {
        this.props.countries.getItems();
      });
    } else if (isOpen) {
      this.setState({ countryDropdownOpen: true });
    } else if (!isOpen && this.props.countries.searchTerm !== '') {
      this.setState({ countryDropdownOpen: false }, () => {
        this.props.countries.searchTermChange('');
      });
    } else if (!isOpen) {
      this.setState({ countryDropdownOpen: false });
    }
  }

  @boundMethod
  selectCountry(country: CountryDropdownItem): void {
    this.setState(
      {
        countryISOSelected: country.id.toLowerCase(),
        countryCodeSelected: `${
          country.phone.startsWith('+') ? country.phone : `+${country.phone}`
        }`,
        countryDropdownOpen: false,
      },
      () => {
        if (this.props.searchValue.length > 0) {
          this.props.setSearchUserValue(this.state.countryCodeSelected);
        }
        this.props.countries.searchTermChange('');
        this.focusInput();
        ampli.countryCodeSelected();
      },
    );
  }

  @boundMethod
  onSearchCountryCode(input?: string): void {
    this.props.countries.searchTermChange(input);
  }

  @boundMethod
  showDropdown(searchValue: string): boolean {
    if (this.props.isResolvingDefaultNumber === true) return false;
    return (
      searchValue === '' ||
      containsNumber(searchValue) ||
      searchValue.startsWith('+') ||
      searchValue.startsWith('-') ||
      searchValue.startsWith('(')
    );
  }

  render() {
    let requesters;
    let helpText = <p>Type a phone number or search your contacts</p>;
    let disabled;
    let showSpinner = this.props.isResolvingDefaultNumber === true;
    const prefix = null;
    let tooltip = '';

    const {
      profile,
      editUser,
      fromNumberFormat,
      canMakeOutBoundCall,
      isOnTrial,
      searchValue,
    } = this.props;

    const listUsersProps = {
      profile,
      editUser,
      fromNumberFormat,
      canMakeOutBoundCall,
      highlightedIndex: this.state.highlightedIndex,
    };

    const hasChannelSetupAccess = accessControl.hasAccess(
      'settings.integration.channel.setup',
    );

    const verifiedNumbers = filter(this.props.dynamicTelephoneNumbersArray, {
      outgoing_functionality: true,
    });
    // `verifiedNumbers` is filtered from the dynamic numbers slice, which is
    // only populated once the paginated numbers query fires (typically when
    // the user expands the own-numbers selector). During startup we may
    // already have a selected outbound-capable from-number via the
    // initialization hook even though the dynamic slice is still empty.
    // Treat that as proof we have at least one verified outbound number so
    // the dialer does not briefly claim "no verified number for outgoing
    // calls" while the dynamic slice catches up.
    const hasVerifiedOutboundNumber =
      verifiedNumbers.length > 0 ||
      this.props.selectedFromNumber?.outgoing_functionality === true;

    if (
      isOnTrial &&
      this.props.meta.dynamicTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      !hasVerifiedOutboundNumber &&
      this.props.meta.staticTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      this.props.staticTelephoneNumbersArray.length > 0
    ) {
      let link;
      if (hasChannelSetupAccess) {
        link = (
          <a
            href={'https://' + config.domain.web + '/demo'}
            rel="noopener noreferrer"
            target="_blank"
          >
            Please book a meeting with our sales team.
          </a>
        );
      }
      disabled = 'disabled';
      helpText = (
        <p>
          Your number(s) can only be used to test inbound calls. Outbound calls
          are not enabled while you are in trial. {link}{' '}
        </p>
      );
      tooltip = 'Your number(s) can only be used to test inbound calls.';
    } else if (
      isOnTrial === false &&
      this.props.meta.dynamicTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      !hasVerifiedOutboundNumber &&
      this.props.meta.staticTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      this.props.staticTelephoneNumbersArray.length > 0
    ) {
      let link;
      if (hasChannelSetupAccess) {
        link = (
          <Link to="/settings/phone">Buy a number in settings/phone.</Link>
        );
      }
      disabled = 'disabled';
      helpText = (
        <p>You do not have a verified number for outgoing calls. {link}</p>
      );
      tooltip = `Before you can make any calls you need a number that is verified for outgoing calling. `;
    } else if (
      this.props.meta.staticTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      this.props.staticTelephoneNumbersArray.length === 0 &&
      !hasVerifiedOutboundNumber
    ) {
      let link;
      if (hasChannelSetupAccess) {
        link = (
          <Link to="/settings/phone">
            Get your free number in settings/phone.
          </Link>
        );
      }
      disabled = 'disabled';
      helpText = (
        <p>
          Before you can receive calls, you need to buy a phone number. {link}
        </p>
      );
      tooltip = 'Before you can receive calls, you need to buy a phone number.';
    } else if (
      this.props.meta.dynamicTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      hasVerifiedOutboundNumber &&
      this.props.meta.staticTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      this.props.staticTelephoneNumbersArray.length > 0
    ) {
      disabled = '';
      helpText = <p>Type a phone number or search your contacts</p>;
    } else if (
      this.props.meta.staticTelephoneNumbersArrayAsyncStatus === 'loading' &&
      !hasVerifiedOutboundNumber
    ) {
      disabled = 'disabled';
      helpText = <p>Loading...</p>;
      tooltip = 'Loading...';
    } else if (
      this.props.meta.dynamicTelephoneNumbersArrayAsyncStatus === 'loading' &&
      !hasVerifiedOutboundNumber
    ) {
      disabled = 'disabled';
      helpText = <p>Loading...</p>;
      tooltip = 'Loading...';
    } else if (
      this.props.meta.dynamicTelephoneNumbersArrayAsyncStatus === 'loading' &&
      hasVerifiedOutboundNumber
    ) {
      disabled = '';
      helpText = <p>Type a phone number or search your contacts</p>;
    } else if (
      this.props.meta.dynamicTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      !hasVerifiedOutboundNumber &&
      this.props.meta.staticTelephoneNumbersArrayAsyncStatus === 'succeeded' &&
      this.props.staticTelephoneNumbersArray.length > 0
    ) {
      disabled = '';
      helpText = <p>No numbers found. Please widen your search.</p>;
    }

    if (this.props.isResolvingDefaultNumber === true) {
      // Startup: the backend default-outbound-number query is still in flight.
      // Show a loading state instead of a disabled one with a misleading
      // "can't make outbound call" tooltip.
      disabled = 'disabled';
      helpText = (
        <p>{phoneCallDisabledTooltipMessages.resolvingDefaultNumber}</p>
      );
      tooltip = phoneCallDisabledTooltipMessages.resolvingDefaultNumber;
    } else if (this.props.canMakeOutBoundCall === false) {
      disabled = 'disabled';
      // Prefer the structured reason from the availability hook so the dialer
      // says e.g. "Enable the microphone…" instead of the generic fallback.
      tooltip =
        this.props.phoneCallDisabledTooltipText || "Can't make outbound call";
    }

    if (this.props.meta.usersAsyncStatus === 'loading') {
      // We are fetching the users.
      requesters = <div className={styles.requestersContainer}>Loading...</div>;
    } else if (
      (this.state.phoneUsers === undefined ||
        this.state.phoneUsers?.length === 0) &&
      this.props.meta.usersAsyncStatus === 'succeeded' &&
      this.props.searchValue.length >= 2
    ) {
      // We test that value is a valid phone number or not.
      // If valid we create a new user first and then make the call else show "no user found".
      try {
        const phoneNumber = this.props.searchValue;
        if (isNumberValid({ phoneNumber })) {
          const user: SearchUser = {
            id: 'newUser',
            phone_number: numberFormatterE164({
              phoneNumber: phoneNumber.replace(/[^+0-9]/gi, ''),
            }),
            type: 'contact',
            name: null,
            email: null,
            created_at: '',
            avatar_url: null,
            company_id: null,
            default_outbound_number: null,
            anonymized_at: null,
            given_name: null,
            family_name: null,
            has_scim_enabled: false,
            external_id: null,
            org_id: null,
          };
          requesters = [user].map((user: SearchUser, index: number) => (
            <ListUsers
              key={user.id}
              user={user}
              index={index}
              {...(listUsersProps as any)}
              makeCallClickHandler={this.makeCallClickHandler}
              userType="new"
            />
          ));
        } else {
          requesters = (
            <div className={styles.requestersContainer}>
              Sorry, contact not found
            </div>
          );
        }
      } catch (exception: any) {
        Sentry.captureException(exception, {
          level: 'error',
          extra: {
            note: 'SearchUser failed to build requester list',
          },
        });
        requesters = (
          <div className={styles.requestersContainer}>
            Sorry, contact not found
          </div>
        );
      }
    } else if (this.props.meta.usersAsyncStatus === 'failed') {
      requesters = (
        <div className={styles.requestersContainer}>
          Error while fetching user
        </div>
      );
    } else if (this.state.phoneUsers?.length > 0) {
      requesters = this.state.phoneUsers.map(
        (user: PhoneUserDuplicate, index: number) => (
          <ListUsers
            // Duplicates has same ids, add index to prevent duplicate keys
            key={user.id + user.duplicate ? index : ''}
            index={index}
            user={user}
            {...(listUsersProps as any)}
            makeCallClickHandler={this.makeCallClickHandler}
            userType={user.duplicate ? 'additional_phone_duplicate' : 'regular'}
          />
        ),
      );
    } else if (
      this.props.meta.usersAsyncStatus === 'succeeded' &&
      this.props.searchValue.length === 0
    ) {
      requesters = null;
    } else if (this.props.searchValue.length > 0) {
      requesters = null;
    }

    if (
      this.props.telephonyMeta.makeCall === 'loading' ||
      this.props.telephonyMeta.internalCall === 'loading' ||
      this.props.telephonyMeta.makeCallback === 'loading'
    ) {
      showSpinner = true;
      disabled = 'disabled';
      tooltip = 'Calling...';
    }

    const countryDropdown = this.props.countries?.items.map((country) => ({
      id: country.iso,
      phone: country.phone,
      name: <CountryCodeDropdownItem country={country} />,
    }));

    return (
      <ul>
        <li className="search-users-to-call">
          {helpText}
          <Tooltip content={tooltip} disabled={!tooltip}>
            <div className={styles.phoneInput}>
              {prefix}
              <InputText
                placeholder="Enter number or name"
                disabled={disabled}
                name="searchUser"
                value={stripCountryCodePrefix(
                  this.props.searchValue,
                  this.state.countryCodeSelected,
                )}
                onChange={this.onChangeInput}
                onKeyDown={this.searchInputKeyDown}
                ref={this.input}
                {...(this.showDropdown(searchValue)
                  ? {
                      prefix: {
                        type: 'custom',
                        noWrap: true,
                        value: (
                          <FieldAddonDropdown
                            meta={{
                              selectedValue: (
                                <CountryCodeSelected
                                  countryISOSelected={
                                    this.state.countryISOSelected
                                  }
                                  countryCodeSelected={
                                    this.state.countryCodeSelected
                                  }
                                />
                              ),
                            }}
                            hasSearch
                            searchPlaceholder="Search for country"
                            items={countryDropdown}
                            onToggle={this.onToggle}
                            onSelect={this.selectCountry}
                            onSearch={this.onSearchCountryCode}
                            dropdownProps={{
                              style: { width: '300px', left: 0 },
                              onClick: (
                                e: React.MouseEvent<HTMLDivElement>,
                              ) => {
                                e.stopPropagation();
                              },
                            }}
                          />
                        ),
                      },
                    }
                  : undefined)}
                {...(showSpinner
                  ? {
                      prefix: {
                        type: 'custom',
                        value: <Spinner size={18} />,
                      },
                    }
                  : undefined)}
              />
            </div>
          </Tooltip>
        </li>
        {requesters}
      </ul>
    );
  }
}
